import './NodeShortStatus.css';
import { cardTitle } from '../../assets/jss/wisene-material-dashboard-pro-react-types';
import { closeNoMeasurementsWarning } from '../../stores/actions/exportActions';
import { connect } from 'react-redux';
import { CSSProperties, ClassNameMap } from '@material-ui/styles';
import { headerStyleNoMeasurements } from '../../helpers/GlobalVariables';
import { INode, ISystem } from '../../helpers/interfaces';
import { NavLink } from 'react-router-dom';
import { NodeExtendedInfo } from '../NodeExtendedInfo/NodeExtendedInfo';
import { SENSORS } from '../../helpers/Devices';
import { sweetAlertStyle } from '../../helpers/CustomStyles';
import { SYSTEM_VERSIONS } from '../../helpers/Systems';
import BaseNodeCard from '../BaseNodeCard/BaseNodeCard';
import AccelerateNodeCard from '../AccelerateNodeCard/AccelerateNodeCard';
import DeActivateNodeCard from '../DeActivateNodeCard/DeActivateNodeCard';
import Button from '../../../components/CustomButtons/ButtonTS';
import Card from '../../../components/Card/CardTS';
import CardHeader from '../../../components/Card/CardHeaderTS';
import Close from '@material-ui/icons/Close';
import dashboardStyle from '../../assets/jss/views/dashboardStyle';
import EmptyCard from '../EmptyCard/EmptyCard.jsx';
import ExportCard from '../ExportCard/ExportCardTS';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Ink from 'react-ink';
import NodeActionIcons from '../NodeActionIcons/NodeActionIconsTS';
import NodeEditCard from '../NodeEditCard/NodeEditCardTS';
import NodeLevelsCard from '../NodeLevelsCard/NodeLevelsCard';
import NodeImagesCard from '../NodeImagesCard/NodeImagesCard';
import Cast from '@material-ui/icons/Cast';
import CastConnectedOutlined from '@material-ui/icons/CastConnectedOutlined';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import NodeStatusFooter from '../NodeStatusFooter/NodeStatusFooter.jsx';
import React from 'react';
import SweetAlert from 'react-bootstrap-sweetalert';
import WiseneCardIcon from '../WiseneCardIcon/WiseneCardIcon';
import InfoIcon from "@material-ui/icons/InfoOutlined";
import LensOutlined from "@material-ui/icons/LensOutlined";
import NodeStatusIcons from "../NodeStatusIcons/NodeStatusIcons";

const style = {
	cardTitle,
	cardCategory: dashboardStyle.cardCategory,
	stats: dashboardStyle.stats,
};

interface INodeShortStatusProps {
	isEditing: boolean;
	noMeasurementsWarnings: number[];
	classes: ClassNameMap;
	exportStart: string;
	exportStop: string;
	node: INode;
	system: ISystem;
	mobileView: boolean;
	dataTestCounter: string;
	accountCategory: string;
	closeNoMeasurementsWarning: (nodeId: number) => void;
	setCurrentNodeID: (nodeId: number) => void;
}

interface INodeShortStatusState {
	alert: JSX.Element;
	signal: AbortSignal;
	controller: AbortController;
	changed: Date;
}

const deleteNodeStyle: CSSProperties = {
	width: '20px',
	height: '25px',
	minWidth: '25px',
	position: 'absolute',
	right: '5px',
	top: '5px',
	zIndex: 5,
};

class NodeShortStatus extends React.Component<INodeShortStatusProps, INodeShortStatusState> {
	controller = new AbortController();
	signal = this.controller.signal;

	state = { changed: null, alert: null, signal: this.signal, controller: this.controller };

	warningTimeout: ReturnType<typeof setTimeout> = null;
	hideWarningTimeout: ReturnType<typeof setTimeout> = null;
	deleteNodeButtonRef = React.createRef<SVGSVGElement>();

	componentDidMount() {
		const { noMeasurementsWarnings, closeNoMeasurementsWarning } = this.props;
		if (noMeasurementsWarnings.length > 0) {
			noMeasurementsWarnings.forEach((warningId) => {
				closeNoMeasurementsWarning(warningId);
			});
		}
	}

	handleCloseNotification = (warningId: number, name: string): void => {
		const warning = document.querySelector(`.${name}`);
		warning.className = 'hide-no-measurements-animation';
		this.hideWarningTimeout = setTimeout(() => {
			this.props.closeNoMeasurementsWarning(warningId);
		}, 600);
	};

	componentWillUnmount() {
		clearTimeout(this.warningTimeout);
		clearTimeout(this.hideWarningTimeout);
	}

	setCurrentNodeId = (): void => {
		this.props.setCurrentNodeID(this.props.node.id);
	};

	parseThreat(threat: string): string {
		return threat === 'none' || threat === 'unknown_or_NA' ? '--' : threat;
	}

	showBaseCard = (node: INode, system: ISystem): void => {
		const { classes } = this.props;
		const { hidePopup } = this;
		this.setState({
			alert: (
				<SweetAlert title=''
					key="node-base-sweet-alert-card"
					onCancel={hidePopup}
					onConfirm={hidePopup}
					showConfirm={false}
					style={sweetAlertStyle}>
					<BaseNodeCard
						changed={this.state.changed}
						isEditing={this.props.isEditing}
						baseAngle={node.MeasurementBaseAngle}
						baseDistance={node.MeasurementBaseDistance}
						classes={classes}
						hideAlert={hidePopup}
						mainSensorType={node.MainSensorType}
						nodeID={node.id}
						nodeName={node.name}
						systemVersion={system.systemVersion}
						updatedViaWebsiteAt={node.updatedViaWebsiteAt}
						systemID={system.ID}
						measuredAngle={node.MeasuredAngle}
						measuredDistance={node.MeasuredDistance}
					/>
				</SweetAlert>
			),
		});
	}

	showLevelsCard = (node: INode, system: ISystem): void => {
		const { classes } = this.props;
		const { hidePopup } = this;
		this.setState({
			alert: (
				<SweetAlert title=''
					key="levels-sweet-alert-card"
					onCancel={hidePopup}
					onConfirm={hidePopup}
					showConfirm={false}
					style={sweetAlertStyle}>
					<NodeLevelsCard
						classes={classes}
						hideAlert={hidePopup}
						mainSensorType={node.MainSensorType}
						nodeID={node.id}
						nodeName={node.name}
						systemVersion={system.systemVersion}
						updatedViaWebsiteAt={node.updatedViaWebsiteAt}
						threshold={node.PermissibleValue}
						l1={node.ThresholdLevel1}
						l2={node.ThresholdLevel2}
						l3={node.ThresholdLevel3}
						l4={node.ThresholdLevel4}
						inclinometerInverted={node.InclinometerInverted}
						systemID={node.systemId}
					/>
				</SweetAlert>
			),
		});
	}

	showExportCard = (node: INode, system: ISystem): void => {
		const { classes, exportStart, exportStop } = this.props;
		const { hidePopup } = this;
		this.setState({
			alert: (
				<SweetAlert title=''
					key="export-sweet-alert-card"
					onCancel={hidePopup}
					onConfirm={hidePopup}
					showConfirm={false}
					style={sweetAlertStyle}>
					<ExportCard
						classes={classes}
						hideAlert={hidePopup}
						name={node.name}
						nodeID={node.id}
						exportStart={exportStart}
						exportStop={exportStop}
						systemName={system.name}
					/>
				</SweetAlert>
			),
		});
	};

	hidePopup = (): void => {
		var self = this;
		if (!this.props.isEditing) {
			setTimeout(() => {
				self.setState({ alert: null, changed: new Date() });
			}, 100);
			this.setState({ controller: new AbortController() });
		}
	};

	showEditNodeNameCard = (node: INode): void => {
		let { id, name, updatedAt } = node;
		this.setState({
			alert: (
				<SweetAlert
					title=''
					showConfirm={false}
					style={sweetAlertStyle}
					onConfirm={this.hidePopup}
					onCancel={this.hidePopup}
					key='node-edit-sweet-alert'
				>
					<NodeEditCard nodeID={id} name={name} nodeUpdatedAt={updatedAt} hideAlert={this.hidePopup} />
				</SweetAlert>
			),
		});
	};

	showAccelerateCard = (node: INode, system: ISystem): void => {
		const { classes } = this.props;
		const { hidePopup } = this;

		this.setState({
			alert: (
				<SweetAlert title=''
					key="node-accelerate-sweet-alert-card"
					onCancel={hidePopup}
					onConfirm={hidePopup}
					showConfirm={false}
					style={sweetAlertStyle}>
					<AccelerateNodeCard
						classes={classes}
						hideAlert={hidePopup}
						nodeID={node.id}
						nodeName={node.name}
						systemVersion={system.systemVersion}
						updatedViaWebsiteAt={node.updatedViaWebsiteAt}
						acceleratedUntil={node.AcceleratedUntil}
					/>
				</SweetAlert>
			),
		});
	}

	showDeActivateCard = (node: INode): void => {
		const { classes } = this.props;
		const { hidePopup } = this;

		this.setState({
			alert: (
				<SweetAlert title=''
					key="node-accelerate-sweet-alert-card"
					onCancel={hidePopup}
					onConfirm={hidePopup}
					showConfirm={false}
					style={sweetAlertStyle}>
					<DeActivateNodeCard
						classes={classes}
						hideAlert={hidePopup}
						nodeID={node.id}
						nodeName={node.name}
						active={node.active}
						updatedViaWebsiteAt={node.updatedViaWebsiteAt}
					/>
				</SweetAlert>
			),
		});
	}

	showNodeImagesCard = (node: INode): void => {
		const { classes } = this.props;
		const { hidePopup } = this;

		this.setState({
			alert: (
				<SweetAlert title=''
					key="node-images-sweet-alert-card"
					onCancel={hidePopup}
					onConfirm={hidePopup}
					showConfirm={false}
					style={sweetAlertStyle}>
					<NodeImagesCard
						classes={classes}
						hideAlert={hidePopup}
						nodeId={node.id}
						nodeName={node.name}
						updatedViaWebsiteAt={node.updatedViaWebsiteAt}
					/>
				</SweetAlert>
			),
		});
	}

	renderEmptyMessage(classes: ClassNameMap): JSX.Element {
		return <EmptyCard classes={classes} />;
	}

	renderMobileNotifications = (): JSX.Element[] => {
		const { noMeasurementsWarnings, mobileView, closeNoMeasurementsWarning } = this.props;

		if (mobileView && noMeasurementsWarnings.length > 0) {
			return noMeasurementsWarnings.map((currentWarningId, i) => {
				const nodeId = this.props.node.id;
				const key = `no-measurement-notification-${this.props.node.name}-${i}`;
				if (nodeId === currentWarningId) {
					this.warningTimeout = setTimeout(() => {
						closeNoMeasurementsWarning(currentWarningId);
					}, 60000);
					return (
						<h5
							className={`no-measurements-warning show-no-measurements-animation ${key}`}
							style={headerStyleNoMeasurements}
							key={key}
						>
							No measurements in this range
							<Button
								simple
								justIcon
								style={{ width: '24px', height: '24px', padding: '0' }}
								onClick={() => this.handleCloseNotification(currentWarningId, key)}
								data-testid='closeNoMeasurementsWarning'
							>
								<Close style={{ width: '20px', height: '20px', padding: '0', transform: 'translate(15px,-4px)' }} />
							</Button>
						</h5>
					);
				}
				return null;
			});
		}
	};

	renderDeviceIcon = () => {
		const { node } = this.props;
		if (node.MainSensorType === SENSORS.None) {
		    if (node.SystemVersion === SYSTEM_VERSIONS.W2 && node.W2ConnectionStatus === 0 ||
		        node.SystemVersion === SYSTEM_VERSIONS.W4 && Date.now() - Date.parse(node.LatestPacketTimestamp) < 1000 * 60 * 60 * 12) {
                return <CastConnectedOutlined style={{ color: 'white' }}/>
		    } else {
                return <Cast style={{ color: 'white' }}/>
		    }
		} else {
			if (!node.PermissibleValue) {
				return<LensOutlined style={{ color: 'white' }}/>;
			}
            else if (node.LatestKnownThreatLevel === 'none' || node.LatestKnownThreatLevel === 'unknown_or_NA') {
                return <CheckCircleOutline style={{ color: 'white' }}/>
            } else {
                return <ErrorOutline style={{ color: 'white' }}/>
            }
		}
	};

	render() {
		const { classes, node, system, accountCategory, mobileView } = this.props;
		const { LatestKnownThreatLevel, name, LatestKnownCalculatedDeflection, MainSensorType, Temperature1, Temperature2, 
			LatestMeasurementTimestamp, LatestDisplacementMeasurementTimestamp, LatestTemperatureMeasurementTimestamp, PermissibleValue, SystemVersion,
			DeviceRole, active } = node;
		const measurementTimestamp = (new Date(LatestMeasurementTimestamp)).toLocaleString('sv-SE').replace('T', ' ');
		const displacementMeasurementTimestamp = (new Date(LatestDisplacementMeasurementTimestamp)).toLocaleString('sv-SE').replace('T', ' ');
		const temperatureMeasurementTimestamp = (new Date(LatestTemperatureMeasurementTimestamp)).toLocaleString('sv-SE').replace('T', ' ');
		const noneOrUnknown = (lKTL) => lKTL === "none" || lKTL === "unknown_or_NA";
		const threatValue = !active || noneOrUnknown(LatestKnownThreatLevel) ? null : LatestKnownThreatLevel;
		const notConfigured = LatestKnownThreatLevel === "unknown_or_NA" && MainSensorType !== "none";
		const headerMessage = notConfigured ? "Not configured" : active && threatValue ? "Threshold: " : null;
		const infoIcon = headerMessage || notConfigured ? (<InfoIcon style={{height: "100%", margin: "0px"}} width="24" height="24" viewBox="0 0 24 24"/>) : null;
        const headerMessageStyle = { display: 'flex', alignItems: 'center', height: '14px', color: "#6c6c6c", marginBottom: "2px", fontWeight: notConfigured ? "300" : "600" };
		const colors = {
			unknown_or_NA: 'L0',
			none: 'L0',
			L1: 'L1',
			L2: 'L2',
			L3: 'L3',
			L4: 'L4',
			obstructed: 'obstructed',
			inactive: 'inactive'
		};
		const containerStyle: CSSProperties = {
			color: '#6c6c6c',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		};
		const iconsContainerStyle = {
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			paddingTop: '20px',
			flexDirection: mobileView ? 'column' : 'row',
			flexWrap: 'wrap'
		};
		const inkProps = { style: { color: 'white' }, duration: 400, radius: 1250 };
		const wiseneCardIconStyle: CSSProperties = { marginBottom: '10px', cursor: 'pointer', position: 'relative' };
		const isInclinometer = MainSensorType === 'inclinometer';
		const deflectionValid = LatestKnownCalculatedDeflection !== undefined && LatestKnownCalculatedDeflection !== null;
		const deflectionPercent = deflectionValid && PermissibleValue ? Math.round(LatestKnownCalculatedDeflection * 100 / PermissibleValue) : 0;
		const deflectionValue = deflectionValid ? LatestKnownCalculatedDeflection.toFixed(isInclinometer ? 3 : 0) : null;
		const unit = isInclinometer ? '°' : 'mm';

		return (
			<>
				{this.state.alert}
				<Card data-testid={`NodeShortStatusHeader-${this.props.dataTestCounter}`}>
					<CardHeader color='info' icon>
						<NavLink exact to={`/devices/${node.id}/graph`}>
							<WiseneCardIcon
								color={
									node.SystemVersion == 'W2' && !node.W2Operational ? 'W2NonOperational' : (
									!node.active ? 'inactive' : 
									colors[LatestKnownThreatLevel] )
								}
								style={wiseneCardIconStyle}
								onClick={() => this.setCurrentNodeId()}
								className='goToGraph'
							>
								{this.renderDeviceIcon()}
								<Ink {...inkProps} />
							</WiseneCardIcon>
						</NavLink>

						<h3 style={{...cardTitle, marginTop: '10px'}}>{name}</h3>
						<div style={style.cardCategory}>
							<GridContainer style={containerStyle}>
								<GridItem xs={12} style={headerMessageStyle}>
									{infoIcon}
									<span style={{flex: '1', whiteSpace: 'nowrap'}}>
                                            {headerMessage}
										{threatValue}
                                        </span>
								</GridItem>
								{DeviceRole === 'gateway' && SystemVersion == SYSTEM_VERSIONS.W2  ? <>
								<>
										<GridItem xs={6}>
											<h5>
												IP address:
												<br/>
												<b>{system.ipAddress ?? '---'}
												</b>
											</h5>
										</GridItem>								
								</> 
								</> :
								SystemVersion == SYSTEM_VERSIONS.W4 && MainSensorType == SENSORS.Iclinometer && ['wisene', 'installer'].includes(accountCategory) ?
								 <>
										<GridItem xs={6}>
											<h5 data-testid='deflection'>
												Deflection:
												<br/>
												<b>{`${!deflectionValid
													? '---'
													: `Deflection: ${deflectionPercent}% (${deflectionValue}${unit})`
												}`}
												</b>
											</h5>
										</GridItem>
										<GridItem xs={6} style={{maxWidth: '100%'}}>
											<h5 data-testid='temperature1'>
												Internal temperature:
												<br/>
												<b>{`${Temperature1 === null || Temperature1 === undefined ? '---' : Temperature1.toFixed(1) + '°C'}`}</b>
											</h5>
										</GridItem>
										<GridItem xs={6} style={{maxWidth: '100%'}}>
											<h5 data-testid='temperature2'>
												Sensor temperature:
												<br/>
												<b>{`${Temperature2 === null || Temperature2 === undefined ? '---' : Temperature2.toFixed(1) + '°C'}`}</b>
											</h5>
										</GridItem>
								</>
								:
								<>
								<GridItem xs={6} style={{maxWidth: '100%'}}>
									<h5 data-testid='deflection'>
										Deflection:
										<br/>
										<b>{`${!deflectionValid
											? '---'
											: `Deflection: ${deflectionPercent}% (${deflectionValue}${unit})`
										}`}
										</b>
									</h5>
								</GridItem>
								<GridItem xs={6} style={{maxWidth: '100%'}}>
								<h5 data-testid='temperature'>
									Temperature:
									<br/>
									<b>{`${Temperature1 === null || Temperature1 === undefined ? '---' : Temperature1.toFixed(1) + '°C'}`}</b>
								</h5>
								</GridItem>
								</>
							}
							{DeviceRole === 'gateway' ? null :
								LatestMeasurementTimestamp !== LatestDisplacementMeasurementTimestamp && MainSensorType !== SENSORS.None ?
									<>
										<GridItem xs={6} style={{maxWidth: '100%'}}>
											<h5 data-testid='latestDisplacementMeasurementTimestamp'>
												Latest deflection:
												<br/>
												<b>{`${LatestDisplacementMeasurementTimestamp ? displacementMeasurementTimestamp : '---'}`}</b>
											</h5>
										</GridItem>
										<GridItem xs={6} style={{maxWidth: '100%'}}>
											<h5 data-testid='latestTemperatureMeasurementTimestamp'>
												Latest temperature:
												<br/>
												<b>{`${LatestTemperatureMeasurementTimestamp ? temperatureMeasurementTimestamp : '---'}`}</b>
											</h5>
										</GridItem>
									</>
									:
									<GridItem xs={6} style={{maxWidth: '100%'}}>
										<h5 data-testid='latestMeasurementTimestamp'>
											Latest measurement:
											<br/>
											<b>{`${LatestMeasurementTimestamp ? measurementTimestamp : '---'}`}</b>
										</h5>
									</GridItem>
							}
							</GridContainer>
						</div>
						<NodeExtendedInfo changed={this.state.changed} node={node} accountCategory={accountCategory}/>
						{this.renderMobileNotifications()}
						<div style={iconsContainerStyle}>
							<NodeStatusIcons
								node={node}
								showRadioCommunicationIcon={true}
								showSensorIcon={node.MainSensorType !== SENSORS.None}
								showTemperatureIcon={true}
								showPowerIcon={true}
								showSignalStrengthIcon={system.systemVersion === SYSTEM_VERSIONS.W4 && node.DeviceRole == 'gateway'}
							/>
							<NodeActionIcons
								node={node}
								system={system}
								mobileView={mobileView}
								showBaseCard={() => this.showBaseCard(node, system)}
								showLevelsCard={() => this.showLevelsCard(node, system)}
								showExportCard={() => this.showExportCard(node, system)}
								showEditNodeNameCard={() => this.showEditNodeNameCard(node)}
								showBaseNodeIcon={system.systemVersion === SYSTEM_VERSIONS.W4 && node.MainSensorType !== SENSORS.None && (accountCategory === 'installer' || accountCategory === 'wisene')}
								showLevelsIcon={system.systemVersion === SYSTEM_VERSIONS.W4 && node.MainSensorType !== SENSORS.None && (accountCategory === 'installer' || accountCategory === 'wisene')}
								showEditIcon={accountCategory !== 'commoner'}
								showDetailsIcon={true}
								showLogsIcon={true}
								showDownloadIcon={true}
								showNodeImagesIcon={true}
								showAccelerateIcon={system.systemVersion === SYSTEM_VERSIONS.W4 && node.MainSensorType !== SENSORS.None}
								showAccelerateCard={() => this.showAccelerateCard(node, system)}
								showDeActivateIcon={system.systemVersion === SYSTEM_VERSIONS.W4 && node.MainSensorType !== SENSORS.None}
								showDeActivateCard={() => this.showDeActivateCard(node)}
								showNodeImagesCard={() => this.showNodeImagesCard(node)}
							/>
						</div>
					</CardHeader>
					<NodeStatusFooter
						node={this.props.node}
					    classes={classes}
					    system={system}
                    />
				</Card>
			</>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		isEditing: state.dashboard.isEditing,
		noMeasurementsWarnings: state.dashboard.noMeasurementsWarnings,
		accountCategory: state.login.accountCategory,
		nodes: state.dashboard.nodes,
		mobileView: state.dashboard.mobileView
	};
};

const actions = { closeNoMeasurementsWarning };

export default connect(mapStateToProps, actions)(NodeShortStatus);
